import { Mutex } from 'async-mutex';
import { deepEqual } from 'fast-equals';
import { useCallback, useEffect, useRef, useState } from 'react';

import { debugConsole, trackError } from 'backend-client-web';
import { ClientCoreParameters, jsonParseThaw, jsonStringifyFreeze } from 'persistable-types';
import {
  symAesDecrypt,
  symAesEncrypt,
  SymAesEncryptedBlob,
  SymAesEncryptionKey,
  wrapSymAesEncryptionKey,
} from 'vault-crypto';

import { IDB_KEYS, getIdbKeyvalEntry, setIdbKeyvalEntry } from './idbKeyvalData';
import { useClientCoreParameters, useEncryptedClientCoreParameters } from './useLocalStorageState';

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;

/**
 * Returns stored ClientCoreParameters and allows updating them.
 *
 * By default CCP are encrypted with a non-extractable crypto key. We fall back to storing them
 * unencrypted in environments that don't support IndexedDB, because we need that to be able to
 * store the non-extractable key.
 *
 * The CCP itself are stored in local storage. We don't use IndexedDB for CCP, because we can't
 * observe IDB for changes. We observe localStorage so we immediately react to stuff like a user
 * deleting their cookies by logging out the user.
 */
export default function useStoredClientCoreParameters() {
  const { getEncryptionKey, getOrCreateEncryptionKey, clearEncryptionKey } = useCCPEncryptionKey();

  const [clientCoreParametersStatus, setClientCoreParametersStatus] = useState<
    | { type: 'loading' }
    | { type: 'set'; clientCoreParameters: ClientCoreParameters }
    | { type: 'empty' }
  >({ type: 'loading' });

  const setClientCoreParametersStatusIfChanged = useCallback(
    (newStatus: typeof clientCoreParametersStatus) =>
      setClientCoreParametersStatus((oldStatus) =>
        deepEqual(oldStatus, newStatus) ? oldStatus : newStatus,
      ),
    [],
  );

  // Originally CCP were stored unencrypted in local storage. We read from there for backwards
  // compatibility and use the unencrypted path as a fallback in case idb or web crypto is not
  // available.
  const [fallbackPlainCCP, setFallbackPlainCCP] = useClientCoreParameters();

  // Storage of encrypted CCP. This can be one of two formats:
  // 1. Modern format using symAesEncrypt, containing the encrypted CCP and IV. We always write this
  //    format and migrate users of the old format to it on load.
  // 2. In the legacy format, only the encrypted CCP are stored in localStorage, while the key and
  //    the IV are in IDB. This means the IV can incorrectly be re-used between for multiple
  //    different plaintexts.
  const [encryptedCCP, setEncryptedCCP] = useEncryptedClientCoreParameters();

  const setClientCoreParametersAsync = useCallback(
    async (newCCPInput: ClientCoreParameters | undefined) => {
      // Stringify and re-parse passed in CCP once. This ensure that the CCP we set into
      // clientCoreParametersStatus here is exactly equal to the one we get from localStorage after
      // we save it there. Without this the passed in CCP may differ, for example objects can have a
      // prototype or properties set to undefined. Both get lost during JSON stringify.
      const newCCP =
        newCCPInput ?
          ClientCoreParameters.parse(jsonParseThaw(jsonStringifyFreeze(newCCPInput)))
        : undefined;

      setClientCoreParametersStatusIfChanged(
        newCCP ? { type: 'set', clientCoreParameters: newCCP } : { type: 'empty' },
      );

      if (!newCCP) {
        setEncryptedCCP(undefined);
        setFallbackPlainCCP(undefined);
        await clearEncryptionKey();
        return;
      }

      // Write encrypted
      try {
        const key = await getOrCreateEncryptionKey();
        setEncryptedCCP(await encryptClientCoreParameters(key, newCCP));
        setFallbackPlainCCP(undefined);
        return;
      } catch (e) {
        debugConsole.log(
          '[useStoredClientCoreParameters]',
          'bailing from writing encrypted CCP due to error',
          e,
        );
        trackError(new Error('bailing from writing encrypted CCP due to error', { cause: e }));
      }

      // Fallback write plain
      setEncryptedCCP(undefined);
      setFallbackPlainCCP(newCCP);
      await clearEncryptionKey();
    },
    [
      clearEncryptionKey,
      getOrCreateEncryptionKey,
      setClientCoreParametersStatusIfChanged,
      setEncryptedCCP,
      setFallbackPlainCCP,
    ],
  );

  const setClientCoreParameters = useCallback(
    (newCCPInput: ClientCoreParameters | undefined) => {
      setClientCoreParametersAsync(newCCPInput).catch(trackError);
    },
    [setClientCoreParametersAsync],
  );

  useEffect(() => {
    async function read() {
      if (encryptedCCP) {
        try {
          const key = await getEncryptionKey();
          if (key) {
            const ccpFromEncrypted = await decryptEncryptedClientCoreParameters(key, encryptedCCP);
            setClientCoreParametersStatusIfChanged({
              type: 'set',
              clientCoreParameters: ccpFromEncrypted,
            });
            return;
          }
        } catch (e) {
          debugConsole.log(
            '[useStoredClientCoreParameters]',
            'bailing from reading encrypted CCP due to error',
            e,
          );
        }

        // No key or failed to decrypt with the new key, so we try to migrate. We also migrate if
        // decrypt failed to catch the case where the new key was already written but the page was
        // interrupted before the newly encrypted CCP were written as well.
        try {
          const ccpFromLegacyEncrypted = await getCCPToMigrate(encryptedCCP);
          if (ccpFromLegacyEncrypted) {
            debugConsole.log(
              '[useStoredClientCoreParameters]',
              'trying to migrate from legacy encrypted CCP',
            );
            await setClientCoreParametersAsync(ccpFromLegacyEncrypted);
            // Only clear legacy encryption info after we're sure we've written the CCP (encrypted
            // with the new key), so we no longer need it. We don't await this so we don't continue
            // to read from local storage just because the migration failed.
            clearLegacyEncryptionInfo().catch(trackError);
            // Return here without calling `setClientCoreParametersStatusIfChanged`, it is called
            // inside setClientCoreParameters.
            return;
          }
        } catch (e) {
          debugConsole.log(
            '[useStoredClientCoreParameters]',
            ' error in encrypted CCP migration, continuing to read from local storage fallback',
            e,
          );
        }
      }

      if (fallbackPlainCCP) {
        setClientCoreParametersStatusIfChanged({
          type: 'set',
          clientCoreParameters: fallbackPlainCCP,
        });
        return;
      }

      setClientCoreParametersStatusIfChanged({ type: 'empty' });
    }

    read().catch(trackError);
  }, [
    encryptedCCP,
    fallbackPlainCCP,
    getEncryptionKey,
    setClientCoreParametersAsync,
    setClientCoreParametersStatusIfChanged,
  ]);

  return {
    clientCoreParametersStatus,
    setClientCoreParameters,
  };
}

function useCCPEncryptionKey() {
  const encryptionKeyRef = useRef<SymAesEncryptionKey>();
  const mutexRef = useRef(new Mutex());

  const getEncryptionKeyInternal = useCallback(async () => {
    if (!encryptionKeyRef.current) {
      const encryptionKey = await getIdbKeyvalEntry(IDB_KEYS.clientCoreParametersEncryptionKey);
      if (encryptionKey) {
        encryptionKeyRef.current = encryptionKey;
      }
    }
    return encryptionKeyRef.current;
  }, []);

  const getEncryptionKey = useCallback(
    async () => mutexRef.current.runExclusive(getEncryptionKeyInternal),
    [getEncryptionKeyInternal],
  );

  const getOrCreateEncryptionKey = useCallback(
    async () =>
      mutexRef.current.runExclusive(async () => {
        const encryptionInfoFromStorage = await getEncryptionKeyInternal();
        if (encryptionInfoFromStorage) {
          return encryptionInfoFromStorage;
        }

        const newEncryptionKey = wrapSymAesEncryptionKey(
          // We're using wrapSymAesEncryptionKey instead of newSymAesEncryptionKey so we can set the
          // crypto key to non-extractable (the `false` argument in the call).
          await window.crypto.subtle.generateKey({ name: ALGORITHM, length: KEY_LENGTH }, false, [
            'decrypt',
            'encrypt',
          ]),
        );
        await setIdbKeyvalEntry(IDB_KEYS.clientCoreParametersEncryptionKey, newEncryptionKey);
        encryptionKeyRef.current = newEncryptionKey;
        return encryptionKeyRef.current;
      }),
    [getEncryptionKeyInternal],
  );

  const clearEncryptionKey = useCallback(
    async () =>
      mutexRef.current.runExclusive(async () => {
        encryptionKeyRef.current = undefined;
        await setIdbKeyvalEntry(IDB_KEYS.clientCoreParametersEncryptionKey, undefined);
      }),
    [],
  );

  return {
    getEncryptionKey,
    getOrCreateEncryptionKey,
    clearEncryptionKey,
  };
}

async function decryptEncryptedClientCoreParameters(
  key: SymAesEncryptionKey,
  encrypted: SymAesEncryptedBlob,
) {
  const encoded = await symAesDecrypt(key, encrypted);
  return decodeClientCoreParameters(encoded);
}

function decodeClientCoreParameters(encoded: Uint8Array<ArrayBuffer>) {
  const ccpString = new TextDecoder().decode(encoded);
  const ccpJson = jsonParseThaw(ccpString);
  return ClientCoreParameters.parse(ccpJson);
}

async function encryptClientCoreParameters(
  key: SymAesEncryptionKey,
  clientCoreParameters: ClientCoreParameters,
) {
  const ccpString = jsonStringifyFreeze(clientCoreParameters);
  const encoded = new TextEncoder().encode(ccpString);
  return symAesEncrypt(key, encoded);
}

async function getCCPToMigrate(encrypted: SymAesEncryptedBlob) {
  const encryptionInfo = await getIdbKeyvalEntry(IDB_KEYS.clientCoreParametersEncryptionInfo);
  if (!encryptionInfo) {
    return;
  }
  const encoded = await window.crypto.subtle.decrypt(
    { name: ALGORITHM, iv: encryptionInfo.initializationVector },
    encryptionInfo.symEncryptionKey,
    encrypted,
  );
  return decodeClientCoreParameters(new Uint8Array(encoded));
}

async function clearLegacyEncryptionInfo() {
  await setIdbKeyvalEntry(IDB_KEYS.clientCoreParametersEncryptionInfo, undefined);
}
